import { useEffect, useState } from "react";
import { useLingui } from "@lingui/react/macro";
import { navigate } from "@/lib/Pathname";
import {
  RATE_LIMIT_DISPLAY_MS,
  RATE_LIMIT_RETRY_MS,
  clearRateLimitState,
  readRateLimitState,
} from "@/lib/rateLimitNotice";

/**
 * rate limit(429) 대기 화면. 인증 게이트 바깥에서 렌더되며(App.tsx 가 pathname 으로
 * 가로챈다) **백엔드 요청을 하나도 하지 않는다** — 서버가 요청을 제한하는 동안 카운터가
 * 자연 회복되도록 조용히 기다리는 게 목적이라, 여기서 또 요청을 쏘면 회복을 늦춘다.
 *
 * lib/rateLimitNotice 가 429 감지 시점에 저장해 둔 마감 시각(until)까지 progress bar 를
 * 채우고, 다 차면 저장해 둔 원래 경로로 되돌려 보낸다. 새로고침 등으로 저장 상태가
 * 없으면 기본 대기시간으로 폴백하고 복귀는 홈으로 한다.
 */
export function RateLimitedPage() {
  const { t } = useLingui();

  // 마감 시각/전체 대기시간/복귀 경로. 마운트 시 1회만 읽는다(이후 갱신은 tick 으로).
  const [state] = useState(() => {
    const saved = readRateLimitState();
    if (saved) return saved;
    // 저장 상태가 없는 경우(예: /rate-limited 에서 새로고침) — 기본값으로 폴백. until(실제
    // 복귀 시각)은 재시도 간격, durationMs(카운트다운 표시 총량)는 표시값으로 각각 쓴다.
    const now = Date.now();
    return { from: "/", until: now + RATE_LIMIT_RETRY_MS, durationMs: RATE_LIMIT_DISPLAY_MS };
  });

  const [remainingMs, setRemainingMs] = useState(() => Math.max(0, state.until - Date.now()));

  useEffect(() => {
    const tick = () => {
      const left = Math.max(0, state.until - Date.now());
      setRemainingMs(left);
      if (left <= 0) {
        clearTimeout(timer);
        clearRateLimitState();
        // 저장해 둔 원래 경로로 복귀. 그 화면이 다시 요청을 보내 여전히 429 라면
        // rateLimitNotice 가 이 화면으로 도로 보내므로(카운터가 아직 안 풀린 것),
        // 사용자는 아무것도 안 해도 결국 풀릴 때까지 여기서 기다리게 된다.
        navigate(state.from);
      }
    };
    // setInterval 대신 재귀 setTimeout — 탭이 백그라운드로 갔다 오면 밀린 tick 이
    // 몰아치지 않고 다음 100ms 뒤 한 번만 돈다.
    let timer = setTimeout(function loop() {
      tick();
      timer = setTimeout(loop, 100);
    }, 100);
    return () => clearTimeout(timer);
  }, [state]);

  // 남은 비율(0~1)로 progress 를 채운다. durationMs 가 0 이하인 비정상 상태는 1 로 취급.
  const progress =
    state.durationMs > 0 ? Math.min(1, Math.max(0, 1 - remainingMs / state.durationMs)) : 1;
  const remainingSec = Math.ceil(remainingMs / 1000);

  return (
    <div className="flex h-dvh w-full flex-col items-center justify-center gap-6 px-6">
      <div className="text-center">
        <h1 className="text-lg font-semibold text-foreground">{t`요청이 너무 많아요`}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t`잠시만 기다려 주세요. 곧 다시 이용할 수 있어요.`}
        </p>
      </div>
      <div className="w-full max-w-xs">
        <div
          className="h-2 w-full overflow-hidden rounded-full bg-muted"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress * 100)}
        >
          <div
            className="h-full rounded-full bg-primary transition-[width] duration-100 ease-linear"
            style={{ width: `${progress * 100}%` }}
          />
        </div>
        <p className="mt-2 text-center text-sm tabular-nums text-muted-foreground">
          {t`${remainingSec}초 후 자동으로 돌아갑니다`}
        </p>
      </div>
    </div>
  );
}
